/**
 * Embedding Store Audit — paste a snapshot from Pinecone or pgvector,
 * see the cold-namespace findings and recoverable monthly spend.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Database, Snowflake, Sparkles, AlertOctagon, ChevronRight } from 'lucide-react';
import BrandTitle from '../../components/BrandTitle';

const API = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_INPUT = {
  pinecone_indexes: [
    { name: 'product-search-v2', pod_type: 'p1.x1', pods: 2, vector_count: 1_400_000,
      last_query_at: new Date().toISOString(), last_upsert_at: new Date(Date.now() - 86400e3 * 3).toISOString() },
    { name: 'old-experimental-rag', pod_type: 'p2.x2', pods: 1, vector_count: 850_000,
      last_query_at: new Date(Date.now() - 86400e3 * 75).toISOString(), last_upsert_at: new Date(Date.now() - 86400e3 * 120).toISOString() },
    { name: 'churned-customer-q3', pod_type: 's1.x1', pods: 1, vector_count: 220_000,
      last_query_at: new Date(Date.now() - 86400e3 * 45).toISOString(), last_upsert_at: new Date(Date.now() - 86400e3 * 60).toISOString() },
  ],
  pgvector_tables: [
    { schema_name: 'public', table_name: 'document_embeddings', row_count: 2_400_000, size_gb: 8.4,
      last_query_at: new Date().toISOString(), last_upsert_at: new Date(Date.now() - 86400e3 * 1).toISOString() },
    { schema_name: 'archive', table_name: 'deprecated_v1_embeddings', row_count: 1_100_000, size_gb: 4.1,
      last_query_at: new Date(Date.now() - 86400e3 * 95).toISOString(), last_upsert_at: new Date(Date.now() - 86400e3 * 180).toISOString() },
  ],
};

const SeverityBadge = ({ severity }) => {
  const map = {
    graveyard: 'bg-rose-500/15 text-rose-300',
    cold: 'bg-amber-500/15 text-amber-300',
    warm: 'bg-emerald-500/15 text-cyan-300',
    active: 'bg-emerald-500/15 text-emerald-300',
    unknown: 'bg-slate-500/15 text-slate-300',
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest ${map[severity] || map.unknown}`}>{severity}</span>;
};

export default function EmbeddingAudit() {
  const [pricing, setPricing] = useState(null);
  const [input, setInput] = useState(JSON.stringify(SAMPLE_INPUT, null, 2));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/embedding-audit/pricing`).then(r => r.json()).then(setPricing);
  }, []);

  const runScan = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const parsed = JSON.parse(input);
      const token = localStorage.getItem('token');
      const r = await fetch(`${API}/api/embedding-audit/scan`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(parsed),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setResult(await r.json());
    } catch (e) {
      setError(String(e.message || e));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px"><div className="max-w-6xl mx-auto px-6 py-8" data-testid="embedding-audit-page">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-3">
          <div className="inline-flex w-12 h-12 rounded-xl bg-cyan-500 bg-gradient-to-br from-cyan-500 to-sky-500 items-center justify-center shadow-lg shadow-cyan-500/30">
            <Database className="w-6 h-6 text-white drop-shadow-sm" />
          </div>
          <div>
            <Badge className="bg-emerald-500/15 border border-emerald-500/30 text-cyan-300 text-[10px] font-mono tracking-widest mb-1">WORLD-FIRST</Badge>
            <h1 className="text-3xl font-bold text-slate-900"><BrandTitle>Embedding Store Audit</BrandTitle></h1>
          </div>
        </div>
        <p className="text-base text-slate-700 max-w-3xl leading-relaxed">
          Paste a snapshot of your Pinecone indexes or pgvector tables. We surface <span className="text-white">graveyard namespaces</span> (cold reads + cold writes), and quantify the monthly spend you can recover.
        </p>
      </div>

      <Card className="bg-slate-950 text-slate-100 border-emerald-500/30 mb-8">
        <CardHeader><CardTitle className="text-base text-white">Paste your inventory snapshot</CardTitle></CardHeader>
        <CardContent>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            spellCheck="false"
            data-testid="embedding-input"
            className="w-full h-72 bg-slate-950 border border-slate-700 rounded-md p-3 font-mono text-xs text-slate-200"
          />
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-slate-400">Format: <code className="text-cyan-300">pinecone_indexes[]</code> + <code className="text-cyan-300">pgvector_tables[]</code>. ISO-8601 timestamps for last_query_at / last_upsert_at.</p>
            <Button onClick={runScan} disabled={busy} className="bg-cyan-600 hover:bg-emerald-500 text-white" data-testid="run-scan-btn">
              {busy ? 'Scanning…' : 'Run audit'}
            </Button>
          </div>
          {error && <div className="mt-3 text-sm text-rose-300">{error}</div>}
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="embedding-summary-cards">
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-1">Total Monthly</div><div className="text-2xl font-bold text-white tabular-nums">${result.total_monthly_cost_usd}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/30"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Recoverable / mo</div><div className="text-2xl font-bold text-emerald-300 tabular-nums">${result.total_recoverable_monthly_usd}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/30"><CardContent className="p-4"><div className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest mb-1">Annual Savings</div><div className="text-2xl font-bold text-emerald-300 tabular-nums">${result.annual_recoverable_usd.toLocaleString()}</div></CardContent></Card>
            <Card className="bg-slate-950 text-slate-100 border-emerald-500/20"><CardContent className="p-4"><div className="text-[10px] font-bold text-cyan-300 uppercase tracking-widest mb-1">By Severity</div><div className="text-xs text-slate-300 mt-1">🪦 {result.by_severity.graveyard} · 🥶 {result.by_severity.cold} · 🌡 {result.by_severity.warm} · ✓ {result.by_severity.active}</div></CardContent></Card>
          </div>

          <Card className="bg-slate-950 text-slate-100 border-emerald-500/20">
            <CardHeader><CardTitle className="text-base text-white flex items-center gap-2"><AlertOctagon className="w-4 h-4 text-amber-400" /> Findings ({result.findings_count})</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                {result.findings.map((f, i) => (
                  <div key={i} className="rounded-md border border-emerald-500/20 bg-slate-950 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-white">{f.name}</span>
                          <SeverityBadge severity={f.severity} />
                          <span className="text-[10px] uppercase text-cyan-300">{f.provider}</span>
                        </div>
                        <div className="text-xs text-slate-400">{f.recommendation}</div>
                        <div className="text-[10px] text-slate-500 mt-1">
                          {f.days_since_last_read !== null && <>last read {f.days_since_last_read}d ago · </>}
                          {f.days_since_last_write !== null && <>last write {f.days_since_last_write}d ago</>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-white">${f.monthly_cost_usd}/mo</div>
                        {f.estimated_recoverable_usd > 0 && <div className="text-emerald-300 text-xs font-mono">−${f.estimated_recoverable_usd}/mo</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {pricing && (
        <Card className="bg-slate-950 text-slate-100 border-slate-700/40 mt-8">
          <CardHeader><CardTitle className="text-sm text-slate-400">Pricing reference (Feb 2026)</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs text-slate-500 mb-2"><span className="text-cyan-300">Pinecone</span> pod $/mo: {Object.entries(pricing.pinecone.usd_per_month).map(([k, v]) => <span key={k} className="font-mono mr-3">{k}=${v}</span>)}</div>
            <div className="text-xs text-slate-400"><span className="text-amber-300">pgvector</span>: ${pricing.pgvector.usd_per_gb_month}/GB-month (storage only)</div>
          </CardContent>
        </Card>
      )}
      </div>
    </div>
  );
}
